import { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { FileVideo, FileImage, FileAudio, Play, Volume2, VolumeX, X, Maximize2, Minimize2, Expand, Shrink } from "lucide-react";

// Default + expanded video dimensions. The popover sizes itself to the video
// plus a fixed scrubber strip beneath it.
const VIDEO_W_DEFAULT = 640;
const VIDEO_H_DEFAULT = 360;
const VIDEO_W_EXPANDED = 960;
const VIDEO_H_EXPANDED = 540;
const SCRUBBER_H = 28;
const POPOVER_GAP = 8;

// Module-level registry so only one preview popover is open at a time across
// the page. When a new one opens, the previous one's close handler is invoked.
// We store an object reference rather than the close function directly so we
// can swap the underlying handler without invalidating registry equality.
const previewRegistry: { active: { close: () => void } | null } = { active: null };

interface VideoThumbnailProps {
  file?: File;
  thumbnail?: string;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  className?: string;
  /**
   * When true, shows the play-icon hint even without a `file` prop.
   * Useful for post-upload pages where the File is no longer available.
   */
  showPlayHint?: boolean;
  /**
   * When true, treats the file as a still image — clicking opens a
   * full-size lightbox instead of the video player.
   */
  isImage?: boolean;
  /**
   * When true, treats the file as an audio recording — shows a FileAudio
   * icon (no thumbnail extraction is meaningful for audio) and the
   * popover click is suppressed (no inline preview during upload; the
   * user just picked the file from disk).
   */
  isAudio?: boolean;
}

/**
 * VideoThumbnail
 *
 * For video files: click the thumbnail to open a floating popover with a
 * muted looping video preview, scrubber, mute toggle, expand-to-larger
 * toggle, and a browser fullscreen toggle. Two sizes: default (640×360
 * video) and expanded (960×540).
 *
 * For image files (isImage=true): click the thumbnail to open a full-size
 * lightbox showing the original image. No playback controls.
 *
 * Outside clicks close the preview UNLESS the click landed in a typeable
 * field (input/textarea/select/contenteditable), so users can keep tagging
 * during playback. The X button always closes.
 *
 * Only one preview is ever open page-wide — opening another thumbnail
 * automatically closes the previous one via a module-level registry.
 *
 * Rendered via a React portal so it is never clipped by overflow:hidden.
 */
export default function VideoThumbnail({
  file,
  thumbnail,
  thumbnailWidth,
  thumbnailHeight,
  className = "",
  showPlayHint = false,
  isImage = false,
  isAudio = false,
}: VideoThumbnailProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [size, setSize] = useState<"default" | "expanded">("default");
  const [isFullscreen, setIsFullscreen] = useState(false);
  // For image lightbox: the object URL of the full-size image
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scrubberRef = useRef<HTMLDivElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  // Stable per-instance identity for the single-active-preview registry.
  // The `close` member is swapped to the latest closePopover on every render.
  const instanceRef = useRef<{ close: () => void } | null>(null);
  if (!instanceRef.current) instanceRef.current = { close: () => {} };

  // Dynamic dimensions driven by `size`. Recomputed every render — cheap.
  const popoverW = size === "expanded" ? VIDEO_W_EXPANDED : VIDEO_W_DEFAULT;
  const videoH = size === "expanded" ? VIDEO_H_EXPANDED : VIDEO_H_DEFAULT;
  const popoverH = videoH + SCRUBBER_H;

  // Audio: no inline popover in upload-step rendering. The user just picked
  // the file from disk so previewing it before sending is low value; full
  // playback shows up later in Footage Search via a real <audio controls>.
  const canInteract = isAudio ? false : (!!file || showPlayHint);
  const showHint = canInteract;

  // ── Thumbnail sizing ────────────────────────────────────────────────────────
  const containerStyle =
    thumbnailWidth && thumbnailHeight
      ? {
          width: `${Math.round(64 * thumbnailWidth / thumbnailHeight)}px`,
          height: "64px",
          maxWidth: "96px",
          minWidth: "40px",
        }
      : { width: "64px", height: "48px" };

  // ── Popover positioning (viewport-relative fixed coords) ────────────────────
  const computePopoverPos = useCallback((w: number, h: number) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PAD = 8;

    let top: number;
    if (rect.top - POPOVER_GAP - h >= PAD) {
      top = rect.top - POPOVER_GAP - h;
    } else if (rect.bottom + POPOVER_GAP + h <= vh - PAD) {
      top = rect.bottom + POPOVER_GAP;
    } else {
      top = (vh - h) / 2;
    }

    let left = rect.left + rect.width / 2 - w / 2;

    top  = Math.max(PAD, Math.min(top,  vh - h - PAD));
    left = Math.max(PAD, Math.min(left, vw - w - PAD));

    return { top, left };
  }, []);

  // ── Open / close helpers ────────────────────────────────────────────────────
  // Closes any other VideoThumbnail preview currently open (single-active rule).
  const closeOtherPreviews = useCallback(() => {
    if (previewRegistry.active && previewRegistry.active !== instanceRef.current) {
      previewRegistry.active.close();
    }
    previewRegistry.active = instanceRef.current;
  }, []);

  const openVideoPopover = useCallback(() => {
    if (!file) return;
    closeOtherPreviews();
    setIsMuted(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setPlaybackRate(1);
    setSize("default");
    setPopoverPos(computePopoverPos(VIDEO_W_DEFAULT, VIDEO_H_DEFAULT + SCRUBBER_H));
    setIsOpen(true);

    if (!objectUrlRef.current) {
      objectUrlRef.current = URL.createObjectURL(file);
    }
    requestAnimationFrame(() => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.src = objectUrlRef.current!;
        videoRef.current.play().catch(() => {
          if (videoRef.current) {
            videoRef.current.muted = true;
            setIsMuted(true);
            videoRef.current.play().catch(() => {/* still blocked */});
          }
        });
      }
    });
  }, [file, computePopoverPos, closeOtherPreviews]);

  const openImageLightbox = useCallback(() => {
    if (!file) return;
    closeOtherPreviews();
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setIsOpen(true);
  }, [file, closeOtherPreviews]);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setPopoverPos(null);
    setSize("default");
    if (typeof document !== "undefined" && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = "";
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    if (previewRegistry.active === instanceRef.current) {
      previewRegistry.active = null;
    }
  }, [imageUrl]);

  // Keep the registry's stable handle pointed at the latest closePopover.
  useEffect(() => {
    if (instanceRef.current) instanceRef.current.close = closePopover;
  }, [closePopover]);

  // When the user toggles size, reposition the popover for the new dimensions.
  useEffect(() => {
    if (!isOpen || isImage || isFullscreen) return;
    setPopoverPos(computePopoverPos(popoverW, popoverH));
  }, [size, isOpen, isImage, isFullscreen, popoverW, popoverH, computePopoverPos]);

  // ── Thumbnail click ─────────────────────────────────────────────────────────
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!file) return;
    e.stopPropagation();
    if (isOpen) {
      closePopover();
    } else if (isImage) {
      openImageLightbox();
    } else {
      openVideoPopover();
    }
  }, [file, isOpen, isImage, openImageLightbox, openVideoPopover, closePopover]);

  // ── X button ────────────────────────────────────────────────────────────────
  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closePopover();
  }, [closePopover]);

  // ── Click outside closes the preview (with several exceptions) ───────────
  // Outside clicks dismiss the popover so it doesn't linger after the user
  // is done. Exceptions, in order of check:
  //   1. Click inside the popover itself  → keep open (own controls)
  //   2. Click on the originating thumb   → its own onClick handles toggle
  //   3. Click into any typeable field    → keep open (let the user type)
  //   4. Click inside a [data-preview-keep-open] subtree → keep open
  //      (covers the activity/topic tag UI — chips, dropdown items, the
  //      "Suggested from this session" chips, the Add button — so users
  //      can tag a clip while it's playing without losing their place).
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && popoverRef.current && popoverRef.current.contains(target)) return;
      if (target && containerRef.current && containerRef.current.contains(target)) return;
      const el = target as HTMLElement | null;
      if (el) {
        const tag = el.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          el.isContentEditable
        ) {
          return;
        }
        if (el.closest("[data-preview-keep-open]")) return;
      }
      closePopover();
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, closePopover]);

  // ── Spacebar to play/pause (video only) ────────────────────────────────────
  useEffect(() => {
    if (!isOpen || isImage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== " ") return;
      // Don't hijack the spacebar when the user is typing in a field — let the
      // space character through to the input so tag editing works during playback.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      e.preventDefault();
      const vid = videoRef.current;
      if (!vid) return;
      if (vid.paused) {
        vid.play().catch(() => {});
      } else {
        vid.pause();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, isImage]);

  // ── Size toggle (default ↔ expanded) ───────────────────────────────────────
  const toggleSize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSize((prev) => (prev === "expanded" ? "default" : "expanded"));
  }, []);

  // ── Fullscreen toggle (browser-native) ─────────────────────────────────────
  const toggleFullscreen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const el = popoverRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  // Mirror the browser's fullscreen state into React so the icon swaps.
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // ── Mute toggle ─────────────────────────────────────────────────────────────
  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  // ── Playback speed cycler (1x → 2x → 3x → 1x) ──────────────────────────────
  const cyclePlaybackRate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPlaybackRate((prev) => {
      const next = prev >= 3 ? 1 : prev + 1;
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  // Re-apply playback rate after the source loads (the <video> resets it).
  useEffect(() => {
    if (!isOpen || isImage) return;
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [isOpen, isImage, playbackRate, duration]);

  // ── Seek helpers ────────────────────────────────────────────────────────────
  const seekFromClientX = useCallback((clientX: number) => {
    const el = scrubberRef.current;
    const vid = videoRef.current;
    if (!el || !vid || !vid.duration) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    vid.currentTime = ratio * vid.duration;
    setProgress(ratio);
  }, []);

  const handleScrubberMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsScrubbing(true);
    seekFromClientX(e.clientX);
  }, [seekFromClientX]);

  useEffect(() => {
    if (!isScrubbing) return;
    const onMove = (e: MouseEvent) => seekFromClientX(e.clientX);
    const onUp = () => setIsScrubbing(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [isScrubbing, seekFromClientX]);

  // ── Video events ────────────────────────────────────────────────────────────
  const handleTimeUpdate = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    setCurrentTime(vid.currentTime);
    setProgress(vid.currentTime / vid.duration);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    setDuration(vid.duration);
  }, []);

  // ── Format helper ───────────────────────────────────────────────────────────
  const formatTime = (secs: number) => {
    if (!isFinite(secs) || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ── Cleanup on unmount ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return (
    <>
      {/* ── Thumbnail container ── */}
      <div
        ref={containerRef}
        className={`relative rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden ${canInteract && file ? "cursor-pointer" : ""} ${isOpen ? "ring-2 ring-primary ring-offset-1" : ""} ${className}`}
        style={containerStyle}
        onClick={handleClick}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={isImage ? "Photo thumbnail" : isAudio ? "Audio file" : "Video thumbnail"}
            className="w-full h-full object-contain rounded-lg"
          />
        ) : isAudio ? (
          <FileAudio className="w-4 h-4 text-muted-foreground" />
        ) : isImage ? (
          <FileImage className="w-4 h-4 text-muted-foreground" />
        ) : (
          <FileVideo className="w-4 h-4 text-muted-foreground" />
        )}

        {/* Action hint overlay — Play for video, Enlarge for image */}
        {showHint && thumbnail && file && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {isImage ? (
              <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-primary/80" : "bg-black/40"}`}>
                <Maximize2 className="w-2.5 h-2.5 text-white" />
              </div>
            ) : (
              <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-primary/80" : "bg-black/40"}`}>
                <Play className="w-2.5 h-2.5 text-white fill-white ml-0.5" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Image lightbox (portal) ── */}
      {isOpen && isImage && imageUrl &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
            style={{ animation: "fadeInScale 120ms ease-out forwards" }}
          >
            {/* X close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
              title="Close"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            {/* Full-size image */}
            <img
              src={imageUrl}
              alt="Full size photo"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>,
          document.body
        )
      }

      {/* ── Video floating popover (portal) ── */}
      {isOpen && !isImage && file && popoverPos &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[9999] rounded-xl overflow-hidden shadow-2xl border border-white/10 bg-black flex flex-col"
            style={{
              // Browser handles geometry when in fullscreen; pinning top/left
              // to 0 keeps the element behaving if fullscreen exits abruptly.
              top: isFullscreen ? 0 : popoverPos.top,
              left: isFullscreen ? 0 : popoverPos.left,
              width: isFullscreen ? "100vw" : popoverW,
              height: isFullscreen ? "100vh" : popoverH,
              animation: "fadeInScale 120ms ease-out forwards",
            }}
          >
            {/* Video area — flex:1 so it fills whatever vertical space remains
                after the fixed-height scrubber. Works for default, expanded,
                and fullscreen with no extra branching. */}
            <div className="relative flex-1 min-h-0">
              <video
                ref={videoRef}
                muted
                loop
                playsInline
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
              />

              {/* Top-left cluster: expand + fullscreen toggles */}
              <div className="absolute top-2 left-2 flex gap-1 z-10">
                <button
                  onClick={toggleSize}
                  className="w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                  title={size === "expanded" ? "Shrink to default size" : "Expand to larger size"}
                >
                  {size === "expanded"
                    ? <Minimize2 className="w-3.5 h-3.5 text-white" />
                    : <Maximize2 className="w-3.5 h-3.5 text-white" />
                  }
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isFullscreen
                    ? <Shrink className="w-3.5 h-3.5 text-white" />
                    : <Expand className="w-3.5 h-3.5 text-white" />
                  }
                </button>
              </div>

              {/* X close button — top-right */}
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors z-10"
                title="Close preview"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>

              {/* Playback speed cycler — bottom-right, left of mute */}
              <button
                onClick={cyclePlaybackRate}
                className="absolute bottom-2 right-11 h-7 px-2 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors text-[11px] font-semibold text-white tabular-nums"
                title="Playback speed (click to cycle 1x / 2x / 3x)"
              >
                {playbackRate}x
              </button>

              {/* Mute toggle — bottom-right */}
              <button
                onClick={toggleMute}
                className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted
                  ? <VolumeX className="w-3.5 h-3.5 text-white" />
                  : <Volume2 className="w-3.5 h-3.5 text-white" />
                }
              </button>
            </div>

            {/* Scrubber */}
            <div
              ref={scrubberRef}
              className="relative h-7 bg-zinc-900 flex items-center px-3 gap-2 cursor-pointer select-none flex-shrink-0"
              onMouseDown={handleScrubberMouseDown}
            >
              <span className="text-[10px] text-zinc-400 w-8 text-right tabular-nums flex-shrink-0">
                {formatTime(currentTime)}
              </span>
              <div className="flex-1 h-1 bg-zinc-700 rounded-full relative">
                <div
                  className="absolute left-0 top-0 h-full bg-white rounded-full"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-zinc-400 w-8 tabular-nums flex-shrink-0">
                {formatTime(duration)}
              </span>
            </div>
          </div>,
          document.body
        )
      }
    </>
  );
}
